import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Calendar,
  Download,
  Eye,
  MoreVertical,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Box,
  Link2,
  Database,
  ShieldCheck,
  FileText,
  Cpu,
  Package,
  Settings,
  Code2,
  Award,
  ArrowUp,
  Plus,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

const tabs = [
  'Overview',
  'Anchored Records',
  'IPFS Files',
  'Verifications',
  'Nodes',
  'Smart Contracts',
  'Settings',
];

const TAB_ROUTES = {
  overview: '/blockchain-anchoring',
  'anchored-records': '/blockchain-anchoring/anchored-records',
  'ipfs-files': '/blockchain-anchoring/ipfs-files',
  verifications: '/blockchain-anchoring/verifications',
  nodes: '/blockchain-anchoring/nodes',
  'smart-contracts': '/blockchain-anchoring/smart-contracts',
  settings: '/blockchain-anchoring/settings',
};

const NEW_RECORD_ROUTE = '/blockchain-anchoring/anchor-new-record';

/* -------------------------------------------------------------------------- */
/* Type styles                                                                */
/* -------------------------------------------------------------------------- */

const TYPE_STYLES = {
  Document: {
    icon: FileText,
    cls: 'bg-violet-50 text-violet-600',
  },
  Firmware: {
    icon: Cpu,
    cls: 'bg-sky-50 text-sky-600',
  },
  Software: {
    icon: Package,
    cls: 'bg-orange-50 text-orange-600',
  },
  Configuration: {
    icon: Settings,
    cls: 'bg-emerald-50 text-emerald-600',
  },
  Code: {
    icon: Code2,
    cls: 'bg-rose-50 text-rose-500',
  },
  Certificate: {
    icon: Award,
    cls: 'bg-amber-50 text-amber-600',
  },
};

/* -------------------------------------------------------------------------- */
/* Summary cards                                                              */
/* -------------------------------------------------------------------------- */

const summaryCards = [
  {
    title: 'Total Anchored Records',
    value: '186',
    change: '18% vs. last month',
    Icon: Box,
    neutral: false,
  },
  {
    title: 'Blockchain Anchored',
    value: '172',
    change: '21% vs. last month',
    Icon: Link2,
    neutral: false,
  },
  {
    title: 'IPFS Stored',
    value: '186',
    change: '18% vs. last month',
    Icon: Database,
    neutral: false,
  },
  {
    title: 'Verification Success Rate',
    value: '100%',
    change: 'No change vs. last month',
    Icon: ShieldCheck,
    neutral: true,
  },
];

/* -------------------------------------------------------------------------- */
/* Filter options                                                             */
/* -------------------------------------------------------------------------- */

const RECORD_TYPES = [
  'All Record Types',
  'Document',
  'Firmware',
  'Software',
  'Configuration',
  'Code',
  'Certificate',
];

const STATUSES = ['All Statuses', 'Anchored', 'Pending', 'Failed'];
const PAGE_SIZE = 10;

/* -------------------------------------------------------------------------- */
/* Records                                                                    */
/* -------------------------------------------------------------------------- */

const baseDetails = {
  network: 'Ethereum (Public)',
  blockNumber: '7,842,551',
  verificationMethod: 'Blockchain + IPFS',
  verifyStatus: 'Verified',
};

const records = [
  {
    id: 'AR-2025-05-31-001',
    fileName: 'contract_agreement.pdf',
    type: 'Document',
    txShort: '0x7f3a9c4e2b6d...8e9f',
    txFull: '0x7f3a9c4e2b6d...8e9f',
    cid: 'QmXn7e9g8JbW2n1f4Zk...',
    cidFull: 'QmXn7e9g8JbW2n1f4Zk9v6b7Q3h5T8a1pZL4m6NBo09pQr',
    fileHash:
      'af7e9c665d6a7e9f1fe28a6d5edf2ab9ae4d56df7ab9e0cfdd213',
    size: '1.2 MB',
    category: 'Document',
    tags: ['contract', 'legal', 'agreement'],
    date: 'May 31, 2025',
    dateISO: '2025-05-31',
    time: '10:42 AM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-31-002',
    fileName: 'firmware_v2.1.bin',
    type: 'Firmware',
    txShort: '0x9a2b4c6d7e8f...3a1c',
    txFull: '0x9a2b4c6d7e8f1a2b3c4d...3a1c',
    cid: 'QmTF8v3h9kL2p6z1...',
    cidFull: 'QmTF8v3h9kL2p6z1Rm4Xc8Yw3Nq5Ub7Ea9Dj2Gs6Hf1Kt',
    fileHash:
      'b1c2d3e4f5a6b7c8d9e0f1a2b3c4d5e6f7a8b9c0d1e2f3a4b5c6',
    size: '156.8 MB',
    category: 'Firmware',
    tags: ['firmware', 'release'],
    date: 'May 31, 2025',
    dateISO: '2025-05-31',
    time: '10:15 AM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-30-045',
    fileName: 'deployment_package.zip',
    type: 'Software',
    txShort: '0x5c7e2d9f1b3a...4c6d',
    txFull: '0x5c7e2d9f1b3a8c4e6f...4c6d',
    cid: 'QmZ8k6p9w1n4t7e2...',
    cidFull: 'QmZ8k6p9w1n4t7e2Qa5Sd8Fg3Hj6Kl9Zx2Cv5Bn8Mq1Wr',
    fileHash:
      'c3d4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8',
    size: '24.6 MB',
    category: 'Software',
    tags: ['deployment', 'package'],
    date: 'May 30, 2025',
    dateISO: '2025-05-30',
    time: '04:32 PM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-30-044',
    fileName: 'k8s_manifest.yaml',
    type: 'Configuration',
    txShort: '0x3d8f6a1c2e7b...9f0a',
    txFull: '0x3d8f6a1c2e7b4d9a5c...9f0a',
    cid: 'QmN1z4g8t3h9e6w2...',
    cidFull: 'QmN1z4g8t3h9e6w2Pk5Lm8Jn3Hb6Vc9Xz2Aq5Sw8Ed1Rf',
    fileHash:
      'd4e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9',
    size: '18 KB',
    category: 'Configuration',
    tags: ['k8s', 'config'],
    date: 'May 30, 2025',
    dateISO: '2025-05-30',
    time: '03:21 PM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-29-038',
    fileName: 'report_final.pdf',
    type: 'Document',
    txShort: '0x8e4c7d2a9f1b...6c3e',
    txFull: '0x8e4c7d2a9f1b5e3a7c...6c3e',
    cid: 'QmR7t3n9p5w8d6...',
    cidFull: 'QmR7t3n9p5w8d6Fg2Hj5Kl8Zx1Cv4Bn7Mq9Ws3Ed6Rt2',
    fileHash:
      'e5f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0',
    size: '3.4 MB',
    category: 'Document',
    tags: ['report', 'final'],
    date: 'May 29, 2025',
    dateISO: '2025-05-29',
    time: '09:12 AM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-28-031',
    fileName: 'iot_device_firmware.bin',
    type: 'Firmware',
    txShort: '0x6b2f9e8d7c1a...5e4d',
    txFull: '0x6b2f9e8d7c1a3b5d7f...5e4d',
    cid: 'QmF6p1t8h4k9w3m7...',
    cidFull: 'QmF6p1t8h4k9w3m7Ns2Bv5Cx8Zl1Km4Jh7Gf9Ds3Aq6Wr',
    fileHash:
      'f6a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1',
    size: '88.2 MB',
    category: 'Firmware',
    tags: ['iot', 'firmware'],
    date: 'May 28, 2025',
    dateISO: '2025-05-28',
    time: '02:45 PM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-27-027',
    fileName: 'source_code.tar.gz',
    type: 'Code',
    txShort: '0x4d7a3c9e1b8f...8c1d',
    txFull: '0x4d7a3c9e1b8f2a6c4e...8c1d',
    cid: 'QmP9k2v7n5t4w8z1...',
    cidFull: 'QmP9k2v7n5t4w8z1Hd3Fg6Jk9Lq2Zx5Cv8Bn1Mw4Er7Ty',
    fileHash:
      'a7b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2',
    size: '42.1 MB',
    category: 'Code',
    tags: ['source', 'archive'],
    date: 'May 27, 2025',
    dateISO: '2025-05-27',
    time: '11:30 AM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-26-024',
    fileName: 'policy_document.pdf',
    type: 'Document',
    txShort: '0x9f1c6e2d4a8b...3d8f',
    txFull: '0x9f1c6e2d4a8b7c5e3a...3d8f',
    cid: 'QmT5r8n9k4w2h6v1...',
    cidFull: 'QmT5r8n9k4w2h6v1Pd7Fs3Gh9Jk2Lz5Xc8Vb1Nm4Qw6Er',
    fileHash:
      'b8c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3',
    size: '2.1 MB',
    category: 'Document',
    tags: ['policy', 'compliance'],
    date: 'May 26, 2025',
    dateISO: '2025-05-26',
    time: '04:10 PM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-25-021',
    fileName: 'certificate.crt',
    type: 'Certificate',
    txShort: '0x7c3e9a1d5f8b...6e7a',
    txFull: '0x7c3e9a1d5f8b2c4e6a...6e7a',
    cid: 'QmV6n3t9h5p8w1k2...',
    cidFull: 'QmV6n3t9h5p8w1k2Rd4Fg7Hj9Kl3Zx6Cv1Bn5Mq8Ws2Er',
    fileHash:
      'c9d0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4',
    size: '4 KB',
    category: 'Certificate',
    tags: ['certificate', 'x509'],
    date: 'May 25, 2025',
    dateISO: '2025-05-25',
    time: '01:25 PM',
    status: 'Anchored',
  },
  {
    id: 'AR-2025-05-24-018',
    fileName: 'application_build.zip',
    type: 'Software',
    txShort: '0x5e9b3c7d1a2f...4b6e',
    txFull: '0x5e9b3c7d1a2f8e6c4a...4b6e',
    cid: 'QmJ8p2t6n9h4k1w3...',
    cidFull: 'QmJ8p2t6n9h4k1w3Sd5Fg8Hj2Kl6Zx9Cv3Bn7Mq1Wr4Et',
    fileHash:
      'd0e1f2a3b4c5d6e7f8a9b0c1d2e3f4a5b6c7d8e9f0a1b2c3d4e5',
    size: '64.9 MB',
    category: 'Software',
    tags: ['build', 'application'],
    date: 'May 24, 2025',
    dateISO: '2025-05-24',
    time: '10:18 AM',
    status: 'Anchored',
  },
].map((record) => ({
  ...record,
  ...baseDetails,
  verifiedOn: `${record.date} ${record.time}`,
}));

/* -------------------------------------------------------------------------- */
/* Main component                                                             */
/* -------------------------------------------------------------------------- */

export default function AnchoredRecords() {
  const navigate = useNavigate();

  const [activeTab] = useState('anchored-records');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All Record Types');
  const [statusFilter, setStatusFilter] = useState('All Statuses');
  const [selectedRecord, setSelectedRecord] = useState(records[0]);
  const [page, setPage] = useState(1);

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();

    return records.filter((record) => {
      const matchesSearch =
        !query ||
        record.fileName.toLowerCase().includes(query) ||
        record.id.toLowerCase().includes(query) ||
        record.txFull.toLowerCase().includes(query) ||
        record.fileHash.toLowerCase().includes(query) ||
        record.cidFull.toLowerCase().includes(query);

      const matchesType =
        typeFilter === 'All Record Types' ||
        record.type === typeFilter;

      const matchesStatus =
        statusFilter === 'All Statuses' ||
        record.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [search, typeFilter, statusFilter]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRecords.length / PAGE_SIZE)
  );

  const currentPage = Math.min(page, totalPages);

  const visibleRecords = filteredRecords.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const firstRecord =
    filteredRecords.length === 0
      ? 0
      : (currentPage - 1) * PAGE_SIZE + 1;

  const lastRecord = Math.min(
    currentPage * PAGE_SIZE,
    filteredRecords.length
  );

  const pageButtons =
    totalPages > 1 ? [1, 2, 3, 4, 5, '...', totalPages] : [1];

  const updateFilter = (setter, value) => {
    setter(value);
    setPage(1);
  };

  const handleExport = () => {
    const headers = [
      'Record ID',
      'File Name',
      'Type',
      'Transaction ID',
      'IPFS CID',
      'Anchor Date',
      'Status',
    ];

    const rows = filteredRecords.map((record) => [
      record.id,
      record.fileName,
      record.type,
      record.txFull,
      record.cidFull,
      `${record.date} ${record.time}`,
      record.status,
    ]);

    const escapeValue = (value) =>
      `"${String(value).replace(/"/g, '""')}"`;

    const csv = [headers, ...rows]
      .map((row) => row.map(escapeValue).join(','))
      .join('\r\n');

    const blob = new Blob(['\uFEFF', csv], {
      type: 'text/csv;charset=utf-8',
    });

    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');

    downloadLink.href = url;
    downloadLink.download = 'anchored_records.csv';

    document.body.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();

    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  const handlePageChange = (nextPage) => {
    setPage(Math.max(1, Math.min(nextPage, totalPages)));
  };

  return (
    <div className="min-h-screen bg-[#f7fbff] text-[#1e3559]">
      {/* Top tabs */}
      <header className="sticky top-0 z-40 bg-white">
        <div className="mx-auto flex h-14 max-w-[1600px] items-center justify-between px-0">
          <nav className="flex gap-6 overflow-x-auto">
            {tabs.map((tab) => {
              const tabKey = tab.toLowerCase().replace(/\s+/g, '-');
              const isActive = activeTab === tabKey;

              return (
                <button
                  type="button"
                  key={tab}
                  onClick={() => navigate(TAB_ROUTES[tabKey])}
                  className={`whitespace-nowrap pb-3 pt-1 text-xs font-medium transition ${
                    isActive
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-gray-900 hover:text-gray-900'
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </nav>

          <button
            type="button"
            onClick={() => navigate(NEW_RECORD_ROUTE)}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-md bg-[#6200ee] px-3 text-[10px] font-medium text-white shadow-sm transition hover:bg-[#5100c9]"
          >
            <Plus className="h-3.5 w-3.5" />
            Anchor New Record
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] space-y-4 px-0 pb-5">
        {/* Summary cards - refactored into individual bordered cards */}
        <section className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {summaryCards.map((card) => {
            const Icon = card.Icon;

            return (
              <div
                key={card.title}
                className="min-w-0 rounded-md border border-[#e5edf7] bg-white px-3 py-2 shadow-sm"
              >
                <div className="flex items-center gap-2">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-[#f2edff]">
                    <Icon
                      className="h-7 w-7 text-[#6426df]"
                      strokeWidth={1.8}
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="truncate text-[11px] font-medium text-[#7290b5] leading-tight">
                      {card.title}
                    </p>

                    <p className="text-xl font-bold leading-tight text-[#20375e]">
                      {card.value}
                    </p>

                    <p
                      className={`text-[9px] leading-tight ${
                        card.neutral
                          ? 'text-[#7b91b0]'
                          : 'text-[#21a675]'
                      }`}
                    >
                      {!card.neutral && (
                        <ArrowUp className="h-3 w-3 inline" strokeWidth={2} />
                      )}
                      {card.change}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </section>

        {/* Table and right details sidebar */}
        <section className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_240px] xl:grid-cols-[minmax(0,1fr)_255px]">
          {/* Table card — filters live inside */}
          <div className="min-w-0 overflow-hidden rounded-md border border-[#e5edf7] bg-white shadow-sm">

            {/* Compact filters bar */}
            <div className="flex flex-wrap items-center gap-1.5 border-b border-[#edf2f8] bg-[#f8fbff] px-3 py-2">
              <div className="relative min-w-[160px] flex-1">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[#86a0be]" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => updateFilter(setSearch, event.target.value)}
                  placeholder="Search records..."
                  className="h-6 w-full rounded border border-[#dfe9f4] bg-white pl-6 pr-2 text-[10px] text-[#49678f] outline-none placeholder:text-[#91a5bf] focus:border-[#8d62ea]"
                />
              </div>
              <div className="relative">
                <select value={typeFilter} onChange={(event) => updateFilter(setTypeFilter, event.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {RECORD_TYPES.map((type) => (<option key={type}>{type}</option>))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <div className="relative">
                <select value={statusFilter} onChange={(event) => updateFilter(setStatusFilter, event.target.value)} className="h-6 appearance-none rounded border border-[#dfe9f4] bg-white pl-2 pr-5 text-[10px] text-[#5b769c] outline-none focus:border-[#8d62ea]">
                  {STATUSES.map((status) => (<option key={status}>{status}</option>))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-1 top-1/2 h-2.5 w-2.5 -translate-y-1/2 text-[#87a0be]" />
              </div>
              <button type="button" className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] text-[#5b769c] hover:bg-white">
                <Calendar className="h-3 w-3 text-[#718daf]" />
                May 1 – May 31, 2025
                <ChevronDown className="h-2.5 w-2.5 text-[#87a0be]" />
              </button>
              <button type="button" onClick={handleExport} className="flex h-6 items-center gap-1 rounded border border-[#dfe9f4] bg-white px-2 text-[10px] font-medium text-[#6426df] hover:bg-violet-50">
                <Download className="h-3 w-3" />
                Export
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[950px] w-full table-fixed border-collapse">
                {/* Fixed width columns including visible # column */}
                <colgroup>
                  <col className="w-[42px]" />
                  <col className="w-[135px]" />
                  <col className="w-[155px]" />
                  <col className="w-[110px]" />
                  <col className="w-[130px]" />
                  <col className="w-[130px]" />
                  <col className="w-[105px]" />
                  <col className="w-[85px]" />
                  <col className="w-[75px]" />
                </colgroup>

                <thead>
                  <tr className="h-9 border-b border-[#edf2f8] bg-[#f8fbff] text-left text-[10px] font-semibold uppercase tracking-wide text-[#7791b3]">
                    {/* Visible serial-number header */}
                    <th className="w-[42px] min-w-[42px] px-3 text-center">
                      #
                    </th>

                    <th className="px-2">Record ID</th>
                    <th className="px-2">File Name</th>
                    <th className="px-2">Type</th>
                    <th className="px-2">Blockchain TX ID</th>
                    <th className="px-2">IPFS CID</th>
                    <th className="px-2">Anchor Date</th>
                    <th className="px-2">Status</th>

                    <th className="w-[75px] min-w-[75px] px-2 text-center">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {visibleRecords.map((record, index) => {
                    const typeStyle =
                      TYPE_STYLES[record.type] || TYPE_STYLES.Document;

                    const TypeIcon = typeStyle.icon;

                    const isSelected =
                      selectedRecord?.id === record.id;

                    return (
                      <tr
                        key={record.id}
                        onClick={() => setSelectedRecord(record)}
                        className={`h-[49px] cursor-pointer border-b border-[#f0f4f8] transition hover:bg-[#f9fbff] ${
                          isSelected ? 'bg-[#fbfaff]' : 'bg-white'
                        }`}
                      >
                        {/* Visible serial numbers: 1, 2, 3 ... 10 */}
                        <td className="w-[42px] min-w-[42px] px-3 text-center text-xs font-medium text-[#58749c]">
                          {(currentPage - 1) * PAGE_SIZE + index + 1}
                        </td>

                        <td className="px-2">
                          <p
                            title={record.id}
                            className="truncate text-xs font-medium text-[#55739d]"
                          >
                            {record.id}
                          </p>
                        </td>

                        <td className="px-2">
                          <p
                            title={record.fileName}
                            className="truncate text-xs font-medium text-[#46658e]"
                          >
                            {record.fileName}
                          </p>
                        </td>

                        <td className="px-2">
                          <span
                            className={`inline-flex max-w-full items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${typeStyle.cls}`}
                          >
                            <TypeIcon className="h-3 w-3 shrink-0" />
                            <span className="truncate">{record.type}</span>
                          </span>
                        </td>

                        <td className="px-2">
                          <p
                            title={record.txFull}
                            className="truncate font-mono text-[11px] text-[#6f88a8]"
                          >
                            {record.txShort}
                          </p>
                        </td>

                        <td className="px-2">
                          <p
                            title={record.cidFull}
                            className="truncate font-mono text-[11px] text-[#6f88a8]"
                          >
                            {record.cid}
                          </p>
                        </td>

                        <td className="px-2">
                          <p className="text-xs leading-4 text-[#56729a]">
                            {record.date}
                          </p>

                          <p className="text-[11px] leading-4 text-[#8098b6]">
                            {record.time}
                          </p>
                        </td>

                        <td className="px-2">
                          <StatusBadge status={record.status} />
                        </td>

                        <td className="w-[75px] min-w-[75px] px-2">
                          <div className="flex items-center justify-center gap-2">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setSelectedRecord(record);
                              }}
                              title="View record details"
                              className="text-[#7c9fca] transition hover:text-[#6426df]"
                            >
                              <Eye className="h-3.5 w-3.5" />
                            </button>

                            <button
                              type="button"
                              onClick={(event) => event.stopPropagation()}
                              title="More record actions"
                              className="text-[#8ca4c2] transition hover:text-[#6426df]"
                            >
                              <MoreVertical className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {visibleRecords.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="h-72 text-center text-xs text-[#8ca2bd]"
                      >
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex min-h-[52px] flex-wrap items-center justify-between gap-3 border-t border-[#edf2f8] px-3 py-3">
              <p className="text-[9px] text-[#6682aa]">
                Showing {firstRecord}-{lastRecord} of{' '}
                {filteredRecords.length} records
              </p>

              <nav
                aria-label="Anchored records pagination"
                className="flex items-center gap-1"
              >
                <PaginationButton
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  label="Previous page"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </PaginationButton>

                {pageButtons.map((item) =>
                  item === '...' ? (
                    <span
                      key="ellipsis"
                      className="flex h-7 w-7 items-center justify-center rounded border border-[#e2eaf4] text-[9px] text-[#7690b2]"
                    >
                      ...
                    </span>
                  ) : (
                    <PaginationButton
                      key={item}
                      active={currentPage === item}
                      onClick={() => handlePageChange(item)}
                      label={`Page ${item}`}
                    >
                      {item}
                    </PaginationButton>
                  )
                )}

                <PaginationButton
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  label="Next page"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </PaginationButton>
              </nav>
            </div>
          </div>

          {/* Right-side selected record details */}
          {selectedRecord && (
            <RecordDetails
              record={selectedRecord}
              onViewFullDetails={() =>
                navigate(
                  `/blockchain-anchoring/anchored-records/${encodeURIComponent(
                    selectedRecord.id
                  )}`
                )
              }
            />
          )}
        </section>
      </main>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Reusable components                                                        */
/* -------------------------------------------------------------------------- */

function PaginationButton({
  children,
  onClick,
  disabled = false,
  active = false,
  label,
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={`flex h-7 w-7 items-center justify-center rounded border text-[9px] font-medium transition ${
        active
          ? 'border-[#6200ee] bg-[#6200ee] text-white'
          : 'border-[#e2eaf4] bg-white text-[#718caf] hover:bg-[#f7faff]'
      } disabled:cursor-not-allowed disabled:opacity-40`}
    >
      {children}
    </button>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Anchored: 'bg-[#dff7ea] text-[#29996e]',
    Verified: 'bg-[#dff7ea] text-[#29996e]',
    Pending: 'bg-amber-50 text-amber-600',
    Failed: 'bg-rose-50 text-rose-600',
  };

  return (
    <span
      className={`inline-flex rounded px-1.5 py-0.5 text-[8px] font-medium ${
        styles[status] || 'bg-slate-100 text-slate-600'
      }`}
    >
      {status}
    </span>
  );
}

function DetailRow({
  label,
  value,
  children,
  link = false,
  compact = false,
}) {
  return (
    <div className="grid grid-cols-[90px_minmax(0,1fr)] items-start gap-2">
      <span className="text-[11px] leading-[15px] text-[#8198b7]">
        {label}
      </span>

      {children ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="flex min-w-0 items-start gap-1">
          <span
            title={value}
            className={`min-w-0 break-all leading-[15px] ${
              compact ? 'font-mono text-[10px]' : 'text-[11px]'
            } ${link ? 'text-[#6426df]' : 'text-[#55719a]'}`}
          >
            {value}
          </span>

          {link && (
            <ExternalLink className="mt-0.5 h-2.5 w-2.5 shrink-0 text-[#6426df]" />
          )}
        </div>
      )}
    </div>
  );
}

function RecordDetails({ record, onViewFullDetails }) {
  const typeStyle =
    TYPE_STYLES[record.type] || TYPE_STYLES.Document;

  const FileTypeIcon = typeStyle.icon;

  return (
    <aside className="min-w-0 rounded-md border border-[#e5edf7] bg-white p-3 shadow-sm space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-1.5 text-xs font-semibold text-[#29446c]">
          <span className="h-1.5 w-1.5 rounded-full bg-[white]" />
          Record Details
        </h2>

        <StatusBadge status={record.status} />
      </div>

      <div className="flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#f2edff]">
          <FileTypeIcon className="h-4 w-4 text-[#6426df]" />
        </div>

        <div className="min-w-0">
          <h3
            title={record.fileName}
            className="truncate text-xs font-semibold text-[#29446c]"
          >
            {record.fileName}
          </h3>

          <p className="mt-0.5 text-[11px] text-[#8299b8]">
            {record.type} &nbsp;•&nbsp; {record.size}
          </p>
        </div>
      </div>

      <div className="space-y-2 border-t border-[#eaf0f7] pt-2.5">
        <DetailRow label="Record ID" value={record.id} />
        <DetailRow label="Transaction ID" value={record.txFull} link compact />
        <DetailRow label="IPFS CID" value={record.cidFull} link compact />
        <DetailRow label="File Hash" value={record.fileHash} compact />
        <DetailRow label="Category" value={record.category} />
        <DetailRow label="Date" value={record.verifiedOn} />
        <DetailRow label="Tags">
          <div className="flex flex-wrap gap-1">
            {record.tags.map((tag) => (
              <span key={tag} className="rounded bg-[#f0f4f8] px-1.5 py-0.5 text-[10px] text-[#55719a]">
                {tag}
              </span>
            ))}
          </div>
        </DetailRow>
      </div>

      <div className="border-t border-[#eaf0f7] pt-2.5">
        <button
          type="button"
          onClick={onViewFullDetails}
          className="flex h-7 w-full items-center justify-center gap-1 rounded border border-[#a987ed] bg-white text-xs font-medium text-[#6426df] transition hover:bg-[#f7f2ff]"
        >
          View Full Details <ExternalLink className="h-3 w-3" />
        </button>
      </div>
    </aside>
  );
}